import { z } from 'zod'
import { PaymentMethodSchema, ProductStatusSchema } from '@shared/contracts/enums'
import {
  FacebookPixelFieldsSchema,
  GoogleAdsPixelFieldsSchema,
  TiktokPixelFieldsSchema,
  UtmifyPixelFieldsSchema,
} from '@shared/contracts/pixels'
import { MonetaryValueSchema } from '@shared/lib/money/money'
import {
  affiliateInviteTokenFromUrl,
  affiliateInviteUrl,
  productUrl,
} from '@shared/config/public-urls'

export const ProductTypeSchema = z.enum(['course', 'ebook', 'service'])
export const PRODUCT_NAME_MAX_LENGTH = 100
export const PRODUCT_DESCRIPTION_MAX_LENGTH = 1000
export type ProductType = z.infer<typeof ProductTypeSchema>

export const PriceModelSchema = z.enum(['one_time', 'subscription'])
export type PriceModel = z.infer<typeof PriceModelSchema>

export const ProductFeatureSchema = z.enum([
  'funnel',
  'tracking',
  'affiliates',
  'global',
  'coupons',
  'coproduction',
  'email',
  'recurrence',
  'membersArea',
  'postPurchaseVisual',
])
export type ProductFeature = z.infer<typeof ProductFeatureSchema>

export const ProductFeaturesSchema = z.object({
  funnel: z.boolean(),
  tracking: z.boolean().catch(false),
  affiliates: z.boolean(),
  global: z.boolean(),
  coupons: z.boolean(),
  coproduction: z.boolean(),
  email: z.boolean(),
  recurrence: z.boolean(),
  membersArea: z.boolean().catch(false),
  // Absent while the Core does not know the feature; the dashboard then hides it and never sends it.
  postPurchaseVisual: z.boolean().optional(),
})
export type ProductFeatures = z.infer<typeof ProductFeaturesSchema>

export const OfferSchema = z.object({
  id: z.string(),
  name: z.string(),
  price: MonetaryValueSchema,
  status: z.enum(['ACTIVE', 'INACTIVE']),
})
export type Offer = z.infer<typeof OfferSchema>

export const OrderBumpSchema = z.object({
  id: z.string(),
  targetOfferId: z.string(),
  planId: z.string().nullable().catch(null),
  title: z.string(),
  description: z.string(),
  callToAction: z.string(),
  price: MonetaryValueSchema,
  commissionable: z.boolean(),
  imageUrl: z.string(),
})
export type OrderBump = z.infer<typeof OrderBumpSchema>

export const FUNNEL_ROOT_ID = 'product'

export const FunnelBranchSchema = z.enum(['accept', 'reject'])
export type FunnelBranch = z.infer<typeof FunnelBranchSchema>

export const FunnelNodeSchema = z.object({
  id: z.string(),
  kind: z.enum(['upsell', 'downsell']),
  parentId: z.string().catch(FUNNEL_ROOT_ID),
  branch: FunnelBranchSchema.catch('accept'),
  title: z.string(),
  subtext: z.string().catch(''),
  mediaUrl: z.string().catch(''),
  offerId: z.string(),
  price: MonetaryValueSchema,
  acceptLabel: z.string(),
  rejectLabel: z.string(),
  mode: z.enum(['hosted', 'snippet']),
  snippet: z.string(),
  externalPageUrl: z.string().catch(''),
  commissionable: z.boolean().catch(true),
  planId: z.string().nullable().catch(null),
})
export type FunnelNode = z.infer<typeof FunnelNodeSchema>

export const CheckoutTemplateSchema = z.enum(['classic', 'minimal'])
export type CheckoutTemplate = z.infer<typeof CheckoutTemplateSchema>

export const CheckoutAccentSchema = z.enum(['green', 'blue', 'violet', 'orange'])
export type CheckoutAccent = z.infer<typeof CheckoutAccentSchema>

export const CheckoutConfigSchema = z.object({
  timer: z.boolean(),
  reviews: z.boolean(),
  bump: z.boolean(),
  security: z.boolean(),
})
export type CheckoutConfig = z.infer<typeof CheckoutConfigSchema>

export const CheckoutFieldsSchema = z
  .object({
    name: z.boolean(),
    email: z.boolean(),
    phone: z.boolean(),
    document: z.boolean(),
  })
  .transform((fields) => ({ ...fields, email: true }))
export type CheckoutFields = z.infer<typeof CheckoutFieldsSchema>

export const CheckoutPresentationSchema = z.object({
  template: CheckoutTemplateSchema,
  accent: CheckoutAccentSchema,
  bannerUrl: z.string(),
  bannerVideoUrl: z.string().optional(),
  blocks: CheckoutConfigSchema,
  fields: CheckoutFieldsSchema,
})

export const ProductCheckoutSchema = z.object({
  id: z.string(),
  productId: z.string(),
  name: z.string(),
  market: z.enum(['national', 'global']),
  template: CheckoutTemplateSchema,
  accent: CheckoutAccentSchema,
  bannerUrl: z.string(),
  bannerVideoUrl: z.string().optional(),
  thankYouUrl: z.string().catch(''),
  blocks: CheckoutConfigSchema,
  fields: CheckoutFieldsSchema,
  legacyPresentation: CheckoutPresentationSchema.nullable().catch(null),
  status: z.enum(['ACTIVE', 'INACTIVE']),
  link: z.string(),
})
export type ProductCheckout = z.infer<typeof ProductCheckoutSchema>

export const UpdateProductCheckoutInputSchema = ProductCheckoutSchema.omit({
  id: true,
  productId: true,
}).partial()
export type UpdateProductCheckoutInput = z.infer<typeof UpdateProductCheckoutInputSchema>

const productPixelFields = {
  id: z.string(),
  sendOnlyCommission: z.boolean().catch(true),
  sendOnlyCommissionLocked: z.boolean().catch(false),
}

export const PixelSchema = z.discriminatedUnion('provider', [
  FacebookPixelFieldsSchema.extend(productPixelFields),
  TiktokPixelFieldsSchema.extend(productPixelFields),
  GoogleAdsPixelFieldsSchema.extend(productPixelFields),
  UtmifyPixelFieldsSchema.extend(productPixelFields),
])
export type Pixel = z.infer<typeof PixelSchema>
export type PixelProvider = Pixel['provider']

export const CouponSchema = z.object({
  id: z.string(),
  code: z.string(),
  type: z.enum(['percent', 'fixed']),
  percent: z.number(),
  amount: MonetaryValueSchema,
  startsAt: z.string(),
  endsAt: z.string(),
  usageLimit: z.number(),
  active: z.boolean(),
})
export type Coupon = z.infer<typeof CouponSchema>

export const CoproducerSchema = z.object({
  id: z.string(),
  email: z.string(),
  percentage: z.string(),
  status: z.enum(['PENDENTE', 'ATIVO', 'REMOVIDO']),
})
export type Coproducer = z.infer<typeof CoproducerSchema>

export function hasReadableEmailButtonContrast(value: string) {
  return contrastRatio(value, '#FFFFFF') >= 3
}

function contrastRatio(foreground: string, background: string) {
  const light = relativeLuminance(foreground)
  const dark = relativeLuminance(background)
  const lighter = Math.max(light, dark)
  const darker = Math.min(light, dark)
  return (lighter + 0.05) / (darker + 0.05)
}

function relativeLuminance(hex: string) {
  const channels = [hex.slice(1, 3), hex.slice(3, 5), hex.slice(5, 7)].map((part) => {
    const channel = Number.parseInt(part, 16) / 255
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  })
  return (channels[0] ?? 0) * 0.2126 + (channels[1] ?? 0) * 0.7152 + (channels[2] ?? 0) * 0.0722
}

export const UPSELL_WIDGET_MIN_BUTTON_CONTRAST = 4.5

export function hasReadableUpsellButtonContrast(buttonColor: string, buttonTextColor: string) {
  return contrastRatio(buttonTextColor, buttonColor) >= UPSELL_WIDGET_MIN_BUTTON_CONTRAST
}

const HexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/)

export const UpsellWidgetStyleSchema = z.object({
  theme: z.enum(['light', 'dark']),
  buttonColor: HexColorSchema,
  buttonTextColor: HexColorSchema,
  radius: z.enum(['square', 'soft', 'rounded', 'pill']),
  font: z.enum(['inter', 'roboto', 'open-sans', 'montserrat', 'poppins', 'lato']),
  textSize: z.enum(['sm', 'md', 'lg']),
})
export type UpsellWidgetStyle = z.infer<typeof UpsellWidgetStyleSchema>

export const DEFAULT_UPSELL_WIDGET_STYLE: UpsellWidgetStyle = {
  theme: 'light',
  buttonColor: '#1E5BFF',
  buttonTextColor: '#FFFFFF',
  radius: 'soft',
  font: 'inter',
  textSize: 'md',
}

export const EmailTemplateSchema = z.object({
  active: z.boolean(),
  subject: z.string(),
  html: z.string(),
  brandColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .refine(
      hasReadableEmailButtonContrast,
      'brandColor deve ter contraste minimo 3.0 com texto branco',
    ),
  logoUrl: z
    .union([z.string().url(), z.literal('')])
    .refine(
      (value) => value === '' || /^https?:\/\//i.test(value),
      'logoUrl deve ser URL http(s) absoluta',
    ),
})
export type EmailTemplate = z.infer<typeof EmailTemplateSchema>

const BillingPeriodUnitSchema = z.enum(['MINUTE', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
const BillingAnchorTypeSchema = z.enum(['IMMEDIATE', 'CALENDAR'])
const TrialTypeSchema = z.enum(['WITH_PAYMENT', 'PAID'])

function normalizeRecurrencePlanInput(input: unknown) {
  if (!input || typeof input !== 'object') return input

  const plan = input as Record<string, unknown>
  const billing =
    typeof plan.billing === 'object' && plan.billing
      ? (plan.billing as Record<string, unknown>)
      : {}
  const trial =
    typeof plan.trial === 'object' && plan.trial ? (plan.trial as Record<string, unknown>) : {}
  const retry =
    typeof plan.retry === 'object' && plan.retry ? (plan.retry as Record<string, unknown>) : {}
  const grace =
    typeof plan.grace === 'object' && plan.grace ? (plan.grace as Record<string, unknown>) : {}
  const value =
    typeof plan.value === 'object' && plan.value ? (plan.value as Record<string, unknown>) : {}
  const currency = typeof value.currency === 'string' ? value.currency : 'BRL'
  const trialDays = plan.trialDays ?? trial.trialDays ?? 0

  return {
    ...plan,
    billingPeriodUnit: plan.billingPeriodUnit ?? billing.billingPeriodUnit ?? 'MONTH',
    billingPeriodCount: plan.billingPeriodCount ?? billing.billingPeriodCount ?? 1,
    billingAnchorType: plan.billingAnchorType ?? billing.billingAnchorType ?? 'IMMEDIATE',
    billingAnchorDayOfMonth:
      plan.billingAnchorDayOfMonth ?? billing.billingAnchorDayOfMonth ?? null,
    trialEnabled:
      plan.trialEnabled ?? trial.isTrialEnabled ?? (typeof trialDays === 'number' && trialDays > 0),
    trialType: plan.trialType ?? trial.trialType ?? 'WITH_PAYMENT',
    trialDays,
    trialPrice: plan.trialPrice ?? trial.trialPrice ?? { amount: '0.000', currency },
    retryAttempts: plan.retryAttempts ?? retry.maxRetryAttempts ?? 3,
    retryDelayDays: plan.retryDelayDays ?? retry.fixedDelayDays ?? 3,
    graceEnabled: plan.graceEnabled ?? grace.gracePeriodEnabled ?? false,
    graceDays: plan.graceDays ?? grace.gracePeriodDays ?? 0,
  }
}

export const RecurrencePlanSchema = z.preprocess(
  normalizeRecurrencePlanInput,
  z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    benefits: z.array(z.string()).catch(() => []),
    value: MonetaryValueSchema,
    billingPeriodUnit: BillingPeriodUnitSchema,
    billingPeriodCount: z.number(),
    billingAnchorType: BillingAnchorTypeSchema,
    billingAnchorDayOfMonth: z.number().nullable().catch(null),
    trialEnabled: z.boolean(),
    trialType: TrialTypeSchema,
    trialDays: z.number(),
    trialPrice: MonetaryValueSchema,
    retryAttempts: z.number(),
    retryDelayDays: z.number(),
    graceEnabled: z.boolean(),
    graceDays: z.number(),
    active: z.boolean(),
  }),
)
export type RecurrencePlan = z.infer<typeof RecurrencePlanSchema>

export const ProductLinksSchema = z.object({
  main: z.string(),
  primaryOfferId: z.string().catch(''),
  affiliateInvite: z.string(),
  offers: z.array(z.object({ offerId: z.string(), name: z.string(), link: z.string() })),
  snippets: z.array(z.object({ nodeId: z.string(), title: z.string(), code: z.string() })),
})
export type ProductLinks = z.infer<typeof ProductLinksSchema>

const productFeatureDefaults = {
  funnel: true,
  tracking: false,
  affiliates: false,
  global: true,
  coupons: false,
  coproduction: false,
  email: false,
  recurrence: false,
  membersArea: false,
} satisfies ProductFeatures

const defaultMoney = { amount: '97.000', currency: 'BRL' as const }

const PRODUCT_CATEGORY_ALIASES: Record<string, string> = {
  educacao: 'Educacao',
}

const PRODUCT_CATEGORY_LABELS: Record<string, string> = {
  educacao: 'Educação',
  negocios: 'Negócios',
  'saude e bem-estar': 'Saúde e bem-estar',
  financas: 'Finanças',
}

function categoryKey(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
}

export function normalizeProductCategory(value: string | undefined): string {
  const trimmed = value?.trim()
  if (!trimmed) return 'Educacao'
  return PRODUCT_CATEGORY_ALIASES[categoryKey(trimmed)] ?? trimmed
}

export function productCategoryLabel(value: string): string {
  const trimmed = value.trim()
  return PRODUCT_CATEGORY_LABELS[categoryKey(trimmed)] ?? trimmed
}

export function productCategoriesMatch(left: string, right: string): boolean {
  return categoryKey(left) === categoryKey(right)
}

function nowIso() {
  return new Date().toISOString()
}

function slugify(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48)
}

function normalizeProductInput(input: unknown) {
  if (!input || typeof input !== 'object') return input

  const product = input as Record<string, unknown>
  const affiliate =
    typeof product.affiliate === 'object' && product.affiliate
      ? (product.affiliate as Record<string, unknown>)
      : undefined
  const links =
    typeof product.links === 'object' && product.links
      ? (product.links as Record<string, unknown>)
      : undefined
  const id = typeof product.id === 'string' ? product.id : 'produto'
  const name = typeof product.name === 'string' ? product.name : 'Produto'
  const slug = slugify(name || id)
  const rawAffiliateInviteLink =
    typeof links?.affiliateInvite === 'string' ? links.affiliateInvite : ''
  const affiliateInviteToken =
    typeof product.affiliateInviteToken === 'string'
      ? product.affiliateInviteToken
      : typeof affiliate?.inviteToken === 'string'
        ? affiliate.inviteToken
        : affiliateInviteTokenFromUrl(rawAffiliateInviteLink)
  const affiliateInviteLink = affiliateInviteToken
    ? affiliateInviteUrl(affiliateInviteToken)
    : rawAffiliateInviteLink

  return {
    ...product,
    category: normalizeProductCategory(
      typeof product.category === 'string' ? product.category : undefined,
    ),
    affiliate: affiliate
      ? {
          ...affiliate,
          inviteToken:
            typeof affiliate.inviteToken === 'string'
              ? affiliate.inviteToken
              : affiliateInviteToken,
        }
      : {
          enabled: product.habilitaAfiliados === true,
          commission:
            typeof product.comissaoAfiliado === 'string' ? product.comissaoAfiliado : '30.000',
          manualApproval:
            typeof product.validarAfiliadoManualmente === 'boolean'
              ? product.validarAfiliadoManualmente
              : true,
          marketplace: false,
          recurring: true,
          buyerData: false,
          inviteToken: affiliateInviteToken,
        },
    links: {
      main: typeof links?.main === 'string' ? links.main : productUrl(slug),
      primaryOfferId: typeof links?.primaryOfferId === 'string' ? links.primaryOfferId : '',
      affiliateInvite: affiliateInviteLink,
      offers: Array.isArray(links?.offers) ? links.offers : [],
      snippets: Array.isArray(links?.snippets) ? links.snippets : [],
    },
    emailTemplate: normalizeEmailTemplate(product.emailTemplate),
  }
}

function defaultEmailTemplate() {
  return {
    active: false,
    subject: 'Compra confirmada',
    html: '<p>Obrigado pela compra. O vendedor enviará as instruções de entrega.</p>',
    brandColor: '#049366',
    logoUrl: '',
  }
}

function normalizeEmailTemplate(input: unknown) {
  const defaults = defaultEmailTemplate()
  const template = input && typeof input === 'object' ? (input as Record<string, unknown>) : {}
  const brandColor = EmailTemplateSchema.shape.brandColor.safeParse(template.brandColor)
  const logoUrl = EmailTemplateSchema.shape.logoUrl.safeParse(template.logoUrl)
  return {
    active: typeof template.active === 'boolean' ? template.active : defaults.active,
    subject: typeof template.subject === 'string' ? template.subject : defaults.subject,
    html: typeof template.html === 'string' ? template.html : defaults.html,
    brandColor: brandColor.success ? brandColor.data : defaults.brandColor,
    logoUrl: logoUrl.success ? logoUrl.data : defaults.logoUrl,
  }
}

const ProductBaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().catch(''),
  productType: ProductTypeSchema.catch('course'),
  category: z.string().catch('Educacao').transform(normalizeProductCategory),
  coverImage: z.string().catch(''),
  supportEmail: z.string().catch(''),
  supportName: z.string().catch(''),
  salesPageUrl: z.string().catch(''),
  status: ProductStatusSchema.catch('DRAFT'),
  features: ProductFeaturesSchema.catch(() => ({ ...productFeatureDefaults })),
  priceModel: PriceModelSchema.catch('one_time'),
  price: MonetaryValueSchema.catch(() => ({ ...defaultMoney })),
  paymentMethods: z.array(PaymentMethodSchema).catch(['CREDIT_CARD', 'APPLE_PAY', 'GOOGLE_PAY']),
  maxInstallments: z.number().catch(1),
  refundDays: z.number().catch(7),
  statementDescriptor: z.string().catch('VENDEPAY'),
  deliveryType: z.enum(['digital', 'members_area', 'external_url']).catch('digital'),
  offers: z.array(OfferSchema).catch(() => []),
  orderBumps: z.array(OrderBumpSchema).catch(() => []),
  funnelNodes: z.array(FunnelNodeSchema).catch(() => []),
  checkouts: z.array(ProductCheckoutSchema).catch(() => []),
  pixels: z.array(PixelSchema).catch(() => []),
  affiliate: z
    .object({
      enabled: z.boolean().catch(false),
      commission: z.string().catch('30.000'),
      manualApproval: z.boolean().catch(true),
      marketplace: z.boolean().catch(false),
      recurring: z.boolean().catch(true),
      buyerData: z.boolean().catch(false),
      inviteToken: z.string().catch(''),
      commissionsFunnel: z.boolean().catch(true),
    })
    .catch(() => ({
      enabled: false,
      commission: '30.000',
      manualApproval: true,
      marketplace: false,
      recurring: true,
      buyerData: false,
      inviteToken: '',
      commissionsFunnel: true,
    })),
  coupons: z.array(CouponSchema).catch(() => []),
  coproducers: z.array(CoproducerSchema).catch(() => []),
  emailTemplate: EmailTemplateSchema,
  // Absent when the Core does not support the setting yet; null when the product keeps the default.
  upsellWidgetStyle: UpsellWidgetStyleSchema.nullable().optional().catch(null),
  recurrencePlans: z.array(RecurrencePlanSchema).catch(() => []),
  links: ProductLinksSchema.catch(() => ({
    main: '',
    primaryOfferId: '',
    affiliateInvite: '',
    offers: [],
    snippets: [],
  })),
  createdAt: z.string().catch(() => nowIso()),
  updatedAt: z.string().catch(() => nowIso()),
})

export const ProductSchema = z.preprocess(normalizeProductInput, ProductBaseSchema)
export type Product = z.infer<typeof ProductSchema>

export const ProductsSchema = z.array(ProductSchema)

export const CreateProductInputSchema = z.object({
  name: z.string().min(1).max(PRODUCT_NAME_MAX_LENGTH),
  productType: ProductTypeSchema,
  category: z.string().min(1).default('Educacao').transform(normalizeProductCategory),
})
export type CreateProductInput = z.infer<typeof CreateProductInputSchema>

export const UpdateProductInputSchema = ProductBaseSchema.partial()
  .extend({
    name: z.string().min(1).max(PRODUCT_NAME_MAX_LENGTH).optional(),
    description: z.string().max(PRODUCT_DESCRIPTION_MAX_LENGTH).optional(),
  })
  .omit({
    id: true,
    status: true,
    coproducers: true,
    recurrencePlans: true,
    links: true,
    createdAt: true,
    updatedAt: true,
  })
export type UpdateProductInput = z.infer<typeof UpdateProductInputSchema>

export function serializeUpdateProductInput(input: UpdateProductInput) {
  const body = UpdateProductInputSchema.parse(input)
  if (!body.affiliate) return body

  const { inviteToken, ...affiliateWithoutInviteToken } = body.affiliate
  const normalizedInviteToken = inviteToken.trim()

  return {
    ...body,
    affiliate: normalizedInviteToken
      ? { ...affiliateWithoutInviteToken, inviteToken: normalizedInviteToken }
      : affiliateWithoutInviteToken,
  }
}

export const checkoutAccentHex: Record<CheckoutAccent, string> = {
  green: '#04D361',
  blue: '#2A6FDB',
  violet: '#7A3BFF',
  orange: '#FF6A2C',
}

export const checkoutAccentSoft: Record<CheckoutAccent, string> = {
  green: 'rgba(4,211,97,.1)',
  blue: 'rgba(42,111,219,.1)',
  violet: 'rgba(122,59,255,.1)',
  orange: 'rgba(255,106,44,.1)',
}

export const checkoutAccentLabels: Record<CheckoutAccent, string> = {
  green: 'Verde',
  blue: 'Azul',
  violet: 'Violeta',
  orange: 'Laranja',
}

export const productTypeLabels: Record<ProductType, string> = {
  course: 'Curso',
  ebook: 'Ebook',
  service: 'Serviço',
}
